import { Student } from "@/types/user.types";
import Close from "@mui/icons-material/Close";
import Google from "@mui/icons-material/Google";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import RelatedStudents from "./RelatedStudents";
import { useAppDispatch } from "@/app/hooks";
import { getStudentByIdentifier } from "@/features/users/usersThunks";
import { Alert, Snackbar } from "@mui/material";

interface GoogleRegisterData {
  documentType: string;
  documentNumber: string;
}

interface GoogleRegisterAdditionalDataProps {
  openModal: boolean;
  handleCloseModal: () => void;
  handleContinue: (data: GoogleRegisterData, students: Student[]) => Promise<void>;
}

export const GoogleRegisterAdditionalData = ({
  openModal,
  handleCloseModal,
  handleContinue,
}: GoogleRegisterAdditionalDataProps) => {
  const dispatch = useAppDispatch();
  const [formData, setFormData] = useState<GoogleRegisterData>({
    documentType: "CC",
    documentNumber: "",
  });

  const [relatedStudents, setRelatedStudents] = useState<Student[]>([]);
  const [errors, setErrors] = useState({
    documentNumber: "",
    students: "",
  });
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState<"success" | "error" | "info">("error");

  const validateForm = (): boolean => {
    const newErrors = { documentNumber: "", students: "" };
    let isValid = true;

    if (!formData.documentNumber.trim()) {
      newErrors.documentNumber = "El documento es requerido";
      isValid = false;
    } else if (!/^\d+$/.test(formData.documentNumber)) {
      newErrors.documentNumber = "Solo se permiten números";
      isValid = false;
    }

    if (relatedStudents.length === 0) {
      newErrors.students = "Debes agregar al menos un estudiante";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleAddStudent = async (newStudent: Student) => {
    // Check if student with same document ID already exists in the list
    const studentExists = relatedStudents.some(
      (student) => student.documentId === newStudent.documentId.trim()
    );

    if (studentExists) {
      setAlertMessage("Este estudiante ya ha sido agregado");
      setAlertSeverity("error");
      setAlertOpen(true);
      return;
    }

    // Validate student exists in database
    try {
      const validatedStudent = await dispatch(
        getStudentByIdentifier(newStudent.documentId.trim())
      ).unwrap();

      const student: Student = {
        id: validatedStudent.id || "",
        name: validatedStudent.displayName,
        documentId: validatedStudent.identifier,
      };

      setRelatedStudents((prevStudents) => [...prevStudents, student]);
      if (errors.students) {
        setErrors((prev) => ({ ...prev, students: "" }));
      }
      setAlertMessage("Estudiante agregado exitosamente");
      setAlertSeverity("success");
      setAlertOpen(true);
    } catch (error: any) {
      setAlertMessage(error || "Estudiante no encontrado en el sistema");
      setAlertSeverity("error");
      setAlertOpen(true);
    }
  };

  const handleDeleteStudent = (id: string) => {
    setRelatedStudents((prevStudents) =>
      prevStudents.filter((student) => student.id !== id)
    );
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      await handleContinue(formData, relatedStudents);

      // Reset form only after successful registration
      setFormData({
        documentType: "CC",
        documentNumber: "",
      });
      setRelatedStudents([]);
      setErrors({ documentNumber: "", students: "" });
    } catch (error) {
      console.error('Error in handleContinue:', error);
      // Don't reset form if there's an error
    }
  };

  const handleClose = () => {
    // Reset form on close
    setFormData({
      documentType: "CC",
      documentNumber: "",
    });
    setRelatedStudents([]);
    setErrors({ documentNumber: "", students: "" });
    handleCloseModal();
  };

  return (
    <Dialog
      open={openModal}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          alignContent: "center",
          pb: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            width: "100%",
            py: 4,
          }}
        >
          <Google sx={{ color: "primary.dark" }} />
          <Typography
            variant="h6"
            fontWeight={600}
            sx={{ textAlign: "center", color: "primary.dark" }}
          >
            Registrarse con Google
          </Typography>
        </Box>

        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: "#666",
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, px: 8 }}>
        {/* Documento de Identidad */}
        <Typography
          fontWeight={550}
          fontSize={14}
          sx={{ mb: 0.5, color: "grey.600", pb: 1 }}
        >
          Documento de Identidad <span style={{ color: "red" }}>*</span>
        </Typography>
        <Box sx={{ display: "flex", gap: 1, mb: 2, pb: 2 }}>
          <Select
            value={formData.documentType}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                documentType: e.target.value,
              }))
            }
            sx={{
              width: "100px",
              backgroundColor: "#fafafa",
              borderRadius: "8px",
              "& fieldset": { border: "none" },
              height: "40px",
            }}
          >
            <MenuItem value="CC">CC</MenuItem>
            <MenuItem value="TI">TI</MenuItem>
            <MenuItem value="CE">CE</MenuItem>
            <MenuItem value="PAS">PAS</MenuItem>
          </Select>
          <TextField
            fullWidth
            placeholder="Número de documento"
            value={formData.documentNumber}
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                documentNumber: e.target.value,
              }));
              if (errors.documentNumber) {
                setErrors((prev) => ({ ...prev, documentNumber: "" }));
              }
            }}
            error={Boolean(errors.documentNumber)}
            helperText={errors.documentNumber}
            InputProps={{
              sx: {
                backgroundColor: "#fafafa",
                borderRadius: "8px",
                "& fieldset": { border: "none" },
                height: "40px",
              },
            }}
          />
        </Box>

        {/* Related Students */}
        <RelatedStudents
          students={relatedStudents}
          handleAddStudent={handleAddStudent}
          handleDeleteStudent={handleDeleteStudent}
        />
        {errors.students && (
          <Typography
            variant="caption"
            color="error"
            sx={{ display: "block", mt: 1 }}
          >
            {errors.students}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={handleSubmit}
          variant="contained"
          startIcon={<Google />}
          sx={{
            fontWeight: 600,
            textTransform: "none",
            backgroundColor: "primary.main",
            "&:hover": {
              backgroundColor: "primary.main",
            },
            color: "white",
          }}
        >
          Continuar con Google
        </Button>
      </DialogActions>

      <Snackbar
        open={alertOpen}
        autoHideDuration={4000}
        onClose={() => setAlertOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setAlertOpen(false)}
          severity={alertSeverity}
          sx={{ width: "100%" }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};
